import cloneDeep from 'lodash.clonedeep';
import { getInitialWidgetPosition, isWidgetInView } from 'modules/Editor/store/initialWidgetPosition';
import { PageId, WidgetId } from 'types/idTypes';
import { Page } from 'types/pageTypes';
import { GroupWidgetData } from 'widgets/GroupWidget/GroupWidget.types';
import { ResponsiveWidgetBaseData } from 'widgets/ResponsiveWidgets/ResponsiveWidgetBase.types';
import { getWidgetTypeFromId } from 'widgets/Widget.helpers';
import { AllWidgetData, NewWidget, WidgetMap } from 'widgets/Widget.types';

import { ActiveWidget, ClipboardGroupWidget, ClipboardWidget, WidgetClipboard } from '../../store/widgetControlSlice';
import { COPY_OFFSET_POSITION } from './Clipboard.config';

export const clearBrowserClipboard = () => navigator?.clipboard?.writeText('');

export const shiftWidgetPosition = (widgetData: AllWidgetData) => {
  const newWidgetData = cloneDeep(widgetData);

  newWidgetData.topPx = widgetData.topPx + COPY_OFFSET_POSITION;
  newWidgetData.leftPx = widgetData.leftPx + COPY_OFFSET_POSITION;

  return newWidgetData;
};

/**
 * Recursive fn to get all group widgets to copy.
 * For groups with responsive widgets, will get the nested member widget ids.
 *
 * Used in createCopyWidgets
 *
 * @param memberWidgetIds member ids of group
 * @param parentId parent id
 * @param isResponsiveGroup True if it is a responsive widget, if true, will set the appropriate componentKey for the groupCopyWidget
 * @param widgets
 * @returns
 */
export const getClipboardGroupWidgets = (
  memberWidgetIds: WidgetId[],
  parentId: WidgetId,
  isResponsiveGroup: boolean,
  widgets: WidgetMap,
): ClipboardGroupWidget[] => {
  let groupWidgets: ClipboardGroupWidget[] = [];
  memberWidgetIds.forEach((memberId: WidgetId) => {
    const widgetData = widgets[memberId] as AllWidgetData;
    const widgetType = getWidgetTypeFromId(memberId);

    let componentKey: string | undefined = undefined;
    if (isResponsiveGroup) {
      const responsiveWData = widgets[parentId] as ResponsiveWidgetBaseData;
      const { componentWidgetIdMap } = responsiveWData;

      componentKey = Object.keys(componentWidgetIdMap).find((key) => componentWidgetIdMap[key] === memberId);
    }

    // Handle the case where a group widget contains a responsive widget with their own respective memberWidgetIds
    let nestedGroupMembers: ClipboardGroupWidget[] = [];
    if (widgetData.hasOwnProperty('memberWidgetIds')) {
      nestedGroupMembers = getClipboardGroupWidgets(
        (widgetData as ResponsiveWidgetBaseData).memberWidgetIds,
        memberId,
        true,
        widgets,
      );
    }

    groupWidgets.push({
      widgetType,
      widgetData,
      ...(componentKey && { componentKey }),
      ...(nestedGroupMembers.length > 0 && { groupMembers: nestedGroupMembers }),
    });
  });

  return groupWidgets;
};

export const createCopyWidgets = (
  activeWidgets: ActiveWidget[],
  widgets: WidgetMap,
  activePageId: PageId,
  widgetLayerOrder: WidgetId[],
) => {
  const copiedWidgets: WidgetClipboard = [];

  // Order the widgets by the widgetLayerOrder on the page so that they are added with the correct
  // layering when pasted.
  const sortedActiveWidgets = cloneDeep(activeWidgets).sort((w1, w2) => {
    const w1Id = w1.groupId || w1.responsiveGroupId || w1.id;
    const w2Id = w2.groupId || w2.responsiveGroupId || w2.id;

    const w1Idx = widgetLayerOrder.findIndex((id) => id === w1Id);
    const w2Idx = widgetLayerOrder.findIndex((id) => id === w2Id);

    if (w1Idx < w2Idx) return -1;
    if (w1Idx > w2Idx) return 1;
    return 0;
  });

  sortedActiveWidgets.forEach((activeWidget: ActiveWidget) => {
    const { id, groupMembers, groupId, responsiveGroupId } = activeWidget;
    const isGroup = !!groupId && !!groupMembers.length;
    const isResponsiveGroup = !groupId && !!responsiveGroupId && !!groupMembers.length;

    const widgetData = widgets[id] as AllWidgetData;

    const parentId = groupId || responsiveGroupId;

    // copy the group widgets + all the widgets in the group
    if (isGroup || isResponsiveGroup) {
      const groupCopyWidget: ClipboardWidget = {
        widgetType: getWidgetTypeFromId(parentId),
        widgetData: widgets[parentId] as AllWidgetData,
        fromPageId: activePageId,
        groupWidgets: [],
        isResponsiveGroup,
        copyFrom: parentId,
      };

      groupCopyWidget.groupWidgets = getClipboardGroupWidgets(groupMembers, parentId, isResponsiveGroup, widgets);

      copiedWidgets.push(groupCopyWidget);
    } else {
      const widgetType = getWidgetTypeFromId(id);
      copiedWidgets.push({ widgetType, widgetData, fromPageId: activePageId, groupWidgets: [], copyFrom: id });
    }
  });

  return copiedWidgets;
};

/**
 * Recursive fn to generate new group widgets to pass to addWidget action.
 * Handles case where there are nested member widgets for groups that have a responsive widget
 *
 * Used in parseCopyWidgets
 */
const generateNewGroupWidgets = (groupWidgets: ClipboardGroupWidget[], isSamePage: boolean): NewWidget[] => {
  const newGroupWidgets: NewWidget[] = groupWidgets.map((groupWidget) => {
    const { widgetData, widgetType, componentKey, groupMembers } = groupWidget;
    const newWidgetData = isSamePage ? shiftWidgetPosition(widgetData) : widgetData;

    let nestedGroupWidgets: NewWidget[] = [];
    if (groupMembers && groupMembers.length > 0) {
      nestedGroupWidgets = generateNewGroupWidgets(groupMembers, isSamePage);
    }

    return { widgetType, widgetData: newWidgetData, componentKey, groupWidgets: nestedGroupWidgets };
  });

  return newGroupWidgets;
};

/**
 * Turns the widgets from the clipboard to NewWidget[] to pass to addWidget action
 * so the widgets can be added to the page.
 *
 * @param widgetClipboard
 * @param activePageId
 *
 * @returns NewWidget[]
 */
export const parseCopyWidgets = (
  widgetClipboard: WidgetClipboard,
  activePageId: PageId,
  shouldKeepInViewport = true,
): NewWidget[] => {
  // 1. check if any of the pasted widgets are partially within the page viewport
  const isWithinView = isPasteWidgetInViewport(widgetClipboard);

  // if is within view port, add the widget normally
  // (with a shifted position for same page widgets)
  const newWidgetArr: NewWidget[] = [];
  if (isWithinView || !shouldKeepInViewport) {
    for (let i = 0; i < widgetClipboard.length; i++) {
      const clipboardWidget = widgetClipboard[i];
      const { widgetType, widgetData, fromPageId, isResponsiveGroup, copyFrom, groupWidgets } = clipboardWidget;
      const isSamePage = fromPageId === activePageId;
      const isGroupWidget = groupWidgets && groupWidgets.length;

      // for groups: shift position of all group member widgets
      if (isGroupWidget) {
        const newGroupWidgets = generateNewGroupWidgets(groupWidgets, isSamePage);
        const newGroupWidget = {
          widgetType,
          widgetData: isResponsiveGroup && isSamePage ? shiftWidgetPosition(widgetData) : widgetData,
          groupWidgets: newGroupWidgets,
          isResponsiveGroup,
          copyFrom,
        };
        newWidgetArr.push(newGroupWidget);
        continue;
      }

      const widget = isSamePage
        ? { widgetType, widgetData: shiftWidgetPosition(widgetData), copyFrom }
        : { widgetType, widgetData, copyFrom };

      newWidgetArr.push(widget);
    }

    return newWidgetArr;
  }

  // find the leftmost widget - add the widgets from the clipboard relative to this position
  const { leftWidget, leftWidgetIdx } = findLeftWidget(widgetClipboard);
  const { idx, groupIdx } = leftWidgetIdx;

  // all the widgets should be added relative to the left most widget within the viewport
  const relativeToWidgetPosition = { top: leftWidget.widgetData.topPx, left: leftWidget.widgetData.leftPx };

  // add the pasted widgets within the viewport
  const { top: initialTopPx, left: initialLeftPx } = getInitialWidgetPosition();

  // move the widgets relative to the left most widget
  for (let i = 0; i < widgetClipboard.length; i++) {
    // the left most widget is not a group widget
    if (i === idx && groupIdx === undefined) {
      const newLeftWidgetData = cloneDeep(leftWidget.widgetData);
      newLeftWidgetData.topPx = initialTopPx;
      newLeftWidgetData.leftPx = initialLeftPx;

      newWidgetArr.push({ ...leftWidget, widgetData: newLeftWidgetData });
      continue;
    }

    const { groupWidgets, isResponsiveGroup, widgetData, widgetType, copyFrom } = widgetClipboard[i];

    // the left most widget is a group widget
    const isGroupWidget = groupWidgets && groupWidgets.length;
    if (isGroupWidget && !isResponsiveGroup) {
      // iterate over the group members
      const hasLeftWidget = groupIdx !== undefined && groupIdx === i;
      let newGroupWidgets: NewWidget[] = [];

      for (let j = 0; j < groupWidgets.length; j++) {
        const { groupMembers, componentKey, widgetType } = groupWidgets[j];
        const newMemberWidgetData = cloneDeep(groupWidgets[j].widgetData);

        // its the left most widget add it to the position within the viewport
        if (j === groupIdx && hasLeftWidget) {
          newMemberWidgetData.topPx = initialTopPx;
          newMemberWidgetData.leftPx = initialLeftPx;
          const newWidget = { widgetData: newMemberWidgetData, groupWidgets: groupMembers, widgetType, componentKey };
          newGroupWidgets.push(newWidget);

          continue;
        }

        // position the member widget relative to the left most widget
        newMemberWidgetData.topPx = initialTopPx + (newMemberWidgetData.topPx - relativeToWidgetPosition.top);
        newMemberWidgetData.leftPx = initialLeftPx + (newMemberWidgetData.leftPx - relativeToWidgetPosition.left);
        const newWidget = { widgetData: newMemberWidgetData, groupWidgets: groupMembers, widgetType, componentKey };
        newGroupWidgets.push(newWidget);
      }

      newWidgetArr.push({ widgetType, widgetData, groupWidgets: newGroupWidgets, copyFrom, isResponsiveGroup });
      continue;
    }

    // move the widget relative from the left most widget
    const newWidgetData = cloneDeep(widgetData);
    newWidgetData.topPx = initialTopPx + (widgetData.topPx - relativeToWidgetPosition.top);
    newWidgetData.leftPx = initialLeftPx + (widgetData.leftPx - relativeToWidgetPosition.left);
    newWidgetArr.push({ ...widgetClipboard[i], widgetData: newWidgetData });
  }

  return newWidgetArr;
};

/**
 * Find the left most widget (by leftPx) from the clipboard
 * (so that the pasted widgets can be added relative to this position)
 *
 * @param clipboardWidget
 * @returns leftWidget
 * @returns leftWidgetIdx
 */
const findLeftWidget = (clipboardWidget: ClipboardWidget[]) => {
  // iterate over the widgets and find the left most widget
  let leftWidget: ClipboardWidget | ClipboardGroupWidget | null = null;
  // if the left most widget is a group widget, groupIdx will be the index of the group member
  let leftWidgetIdx: { idx: number | undefined; groupIdx: undefined | number } = {
    idx: undefined,
    groupIdx: undefined,
  };

  for (let i = 0; i < clipboardWidget.length; i++) {
    const { widgetData, groupWidgets, isResponsiveGroup } = clipboardWidget[i];
    const isGroupWidget = groupWidgets && groupWidgets.length;

    // if its a groupwidget (but not a responsive widget - check the members)
    if (isGroupWidget && !isResponsiveGroup) {
      // iterate over the group members in case any of them are the left most widget
      for (let j = 0; j < groupWidgets.length; j++) {
        const { widgetData } = groupWidgets[j];
        if (!leftWidget || widgetData.leftPx < leftWidget!.widgetData.leftPx) {
          leftWidget = groupWidgets[j];
          leftWidgetIdx.idx = i;
          leftWidgetIdx.groupIdx = j;
        }
      }
      continue;
    }

    // else if its a single widget
    if (!leftWidget || widgetData.leftPx < leftWidget.widgetData.leftPx) {
      leftWidget = clipboardWidget[i];
      leftWidgetIdx.idx = i;
      leftWidgetIdx.groupIdx = undefined;
    }
  }

  return { leftWidget: leftWidget as ClipboardWidget | ClipboardGroupWidget, leftWidgetIdx };
};

/**
 * Check if any of the pasted widgets are within the viewport
 * @param widgetClipboard
 * @returns boolean
 */
function isPasteWidgetInViewport(widgetClipboard: ClipboardWidget[]) {
  let isWithinViewPort = false;

  for (let i = 0; i < widgetClipboard.length; i++) {
    const { widgetData, groupWidgets, isResponsiveGroup } = widgetClipboard[i];

    // check the group widgets
    if (groupWidgets && groupWidgets.length && !isResponsiveGroup) {
      for (let i = 0; i < groupWidgets.length; i++) {
        const { widgetData } = groupWidgets[i];
        isWithinViewPort = isWidgetInView(widgetData);
        if (isWithinViewPort) {
          isWithinViewPort = true;
          break;
        }
      }

      continue;
    }

    // check the normal single widgets
    if (isWidgetInView(widgetData)) {
      isWithinViewPort = true;
      break;
    }
  }

  return isWithinViewPort;
}

/**
 * Recursive fn to copy member widgets in a page
 * Handles cases for nested widgets
 *
 * @param memberWidgetIds
 * @param widgets
 * @param copiedPageWidgets copiedWidgets - is updated directly with the new copied widgets
 */
const copyAllMemberWidgets = (memberWidgetIds: WidgetId[], widgets: WidgetMap, copiedPageWidgets: WidgetMap) => {
  memberWidgetIds.forEach((memberWidgetId: WidgetId) => {
    const copiedMemberWidget = cloneDeep(widgets[memberWidgetId]);
    copiedPageWidgets[memberWidgetId] = copiedMemberWidget;

    if (copiedMemberWidget.hasOwnProperty('memberWidgetIds')) {
      copyAllMemberWidgets((copiedMemberWidget as GroupWidgetData).memberWidgetIds, widgets, copiedPageWidgets);
    }
  });
};

export const createCopyPage = (activePage: Page, widgets: WidgetMap) => {
  const copiedPage = cloneDeep(activePage);

  const { widgetLayerOrder } = copiedPage;
  const copiedPageWidgets: WidgetMap = {};

  // copy the pages widgets (can paste the page even if the source page for the copy has been deleted)
  widgetLayerOrder.forEach((wId: WidgetId) => {
    const copiedWidget = cloneDeep(widgets[wId]);
    copiedPageWidgets[wId] = copiedWidget;

    // copy the group widgets + all the widgets in the group
    const hasMemberWidgets = widgets[wId]?.hasOwnProperty('memberWidgetIds');
    if (hasMemberWidgets) {
      const { memberWidgetIds } = copiedWidget as GroupWidgetData;
      copyAllMemberWidgets(memberWidgetIds, widgets, copiedPageWidgets);
    }
  });

  // store in clipboard
  return [{ page: copiedPage, widgets: copiedPageWidgets }];
};
